(() => {
  const config = document.getElementById('ExternalStockConfig');
  if (!config || customElements.get('external-stock')) return;
  const labels = JSON.parse(config.textContent);
  const cache = new Map();
  const queue = [];
  let active = 0;

  function drain() {
    while (active < 4 && queue.length) {
      const { task, resolve, reject } = queue.shift();
      active++;
      task().then(resolve, reject).finally(() => {
        active--;
        drain();
      });
    }
  }

  function normalize(data, sku) {
    if (!Array.isArray(data)) throw new Error('Invalid stock response');
    const locations = new Map();
    for (const row of data) {
      if (!row || String(row.sku ?? '').trim() !== sku || typeof row.location !== 'string') continue;
      const location = row.location.trim();
      const key = location.toLocaleLowerCase('fi');
      if (!location || locations.has(key)) continue;
      if (!['number', 'string'].includes(typeof row.stock) || String(row.stock).trim() === '') continue;
      const stock = Number(row.stock);
      if (!Number.isFinite(stock)) continue;
      locations.set(key, { location, stock: Math.max(0, stock), online: key === 'lauttasaari' });
    }
    if (!locations.size) throw new Error('Stock data unavailable');
    return Array.from(locations.values());
  }

  function loadStock(sku) {
    const cached = cache.get(sku);
    if (cached && cached.expires > Date.now()) return cached.promise;
    const entry = { expires: Infinity };
    entry.promise = new Promise((resolve, reject) => {
      queue.push({
        resolve,
        reject,
        task: async () => {
          const url = new URL('https://decanter-sapp.viinilehti.fi/fetch_product_stock.php');
          url.searchParams.set('productSKU', sku);
          const controller = new AbortController();
          const timeout = setTimeout(() => controller.abort(), 10000);
          try {
            const response = await fetch(url, { credentials: 'omit', signal: controller.signal });
            if (!response.ok) throw new Error('Stock request failed');
            return normalize(await response.json(), sku);
          } finally {
            clearTimeout(timeout);
          }
        }
      });
      drain();
    }).then(rows => {
      entry.expires = Date.now() + 60000;
      return rows;
    }, error => {
      entry.expires = Date.now() + 10000;
      throw error;
    });
    cache.set(sku, entry);
    return entry.promise;
  }

  function stockLabel(rows, orderProduct, available, mode) {
    const online = rows.find(row => row.online);
    if (orderProduct && (online?.stock === 0 || (!online && mode === 'button'))) return labels.toOrder;
    if (!available && rows.some(row => !row.online && row.stock > 0)) return labels.soldOnline;
    return null;
  }

  class ExternalStock extends HTMLElement {
    connectedCallback() {
      const generation = this.generation = (this.generation || 0) + 1;
      queueMicrotask(() => {
        if (this.isConnected && generation === this.generation) this.initialize();
      });
    }

    initialize() {
      this.mode = this.dataset.mode;
      this.sku = (this.dataset.sku || '').trim();
      this.available = this.dataset.available === 'true';
      this.orderProduct = this.dataset.orderProduct === 'true';
      this.button = this.querySelector('.js-ajax-submit');
      this.content = this.querySelector('[data-stock-content]');
      this.fallback = this.dataset.addLabel || labels.soldOut;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      if (this.mode !== 'badge') {
        this.scope = this.closest('[data-section-type="product__section"], .js-quickview-wrapper');
        this.scope?.addEventListener('product:variant-change', event => {
          const variant = event.detail.variant;
          this.sku = (variant?.sku || '').trim();
          this.available = Boolean(variant?.available);
          this.refresh(Boolean(variant));
        }, { signal });
        this.scope?.addEventListener('change', event => {
          if (!event.target.matches('.js-variant-selector')) return;
          this.generation++;
          this.reset();
        }, { capture: true, signal });
      }
      if (this.mode === 'badge' && 'IntersectionObserver' in window) {
        this.intersection = new IntersectionObserver(entries => {
          if (!entries.some(entry => entry.isIntersecting)) return;
          this.intersection.disconnect();
          this.refresh();
        }, { rootMargin: '200px' });
        this.intersection.observe(this);
      } else {
        this.refresh();
      }
    }

    setMessage(text) {
      if (!this.content) return;
      const message = document.createElement('p');
      message.textContent = text;
      this.content.replaceChildren(message);
    }

    setLabel(text) {
      if (this.button) {
        this.button.value = text;
        this.button.setAttribute('value', text);
      } else if (this.mode === 'badge') {
        this.textContent = text;
      }
    }

    setButtonAvailability(available, label) {
      this.button.dataset.stockUnavailable = String(!available);
      this.button.dataset.stockLabel = label;
      this.button.disabled = !available || this.button.classList.contains('disabled');
      this.setLabel(label);
    }

    reset() {
      if (this.button) {
        this.setButtonAvailability(false, labels.loading);
        return;
      }
      if (this.mode === 'panel') {
        this.hidden = this.orderProduct;
        this.setMessage(labels.loading);
        this.content?.setAttribute('aria-busy', 'true');
      } else {
        this.setLabel(this.fallback);
      }
    }

    async refresh(hasVariant = true) {
      const generation = ++this.generation;
      this.reset();
      if (this.mode === 'panel' && this.orderProduct) return;
      if (!hasVariant || !this.sku) {
        if (this.button) this.setButtonAvailability(false, hasVariant ? labels.unavailable : labels.chooseVariant);
        this.setMessage(hasVariant ? labels.error : labels.chooseVariant);
        this.content?.setAttribute('aria-busy', 'false');
        return;
      }
      try {
        const rows = await loadStock(this.sku);
        if (!this.isConnected || generation !== this.generation) return;
        if (this.button) {
          const online = rows.find(row => row.online);
          if (!online) throw new Error('Online stock unavailable');
          const available = this.available && online.stock > 0;
          const inStore = rows.some(row => !row.online && row.stock > 0);
          this.setButtonAvailability(available, available ? this.fallback : (inStore ? labels.buyInStore : labels.soldOut));
        } else if (this.mode === 'panel') {
          this.renderLocations(rows);
        } else {
          this.setLabel(stockLabel(rows, this.orderProduct, this.available, this.mode) || this.fallback);
        }
      } catch {
        if (!this.isConnected || generation !== this.generation) return;
        if (this.button) this.setButtonAvailability(false, labels.unavailable);
        this.setMessage(labels.error);
      } finally {
        if (this.isConnected && generation === this.generation) this.content?.setAttribute('aria-busy', 'false');
      }
    }

    renderLocations(rows) {
      const list = document.createElement('dl');
      list.className = 'external-stock__locations';
      const maximum = Number(this.dataset.maximum) || 30;
      for (const row of rows) {
        const item = document.createElement('div');
        item.className = 'external-stock__location';
        item.classList.toggle('external-stock__location--available', row.stock > 0);
        const name = document.createElement('dt');
        name.textContent = row.online ? labels.online : row.location;
        const quantity = document.createElement('dd');
        quantity.textContent = row.stock <= 0 ? labels.unavailable :
          (row.stock > maximum ? labels.over : labels.quantity).replace('[count]', String(Math.min(row.stock, maximum)));
        item.append(name, quantity);
        list.append(item);
      }
      this.content.replaceChildren(list);
    }

    disconnectedCallback() {
      this.generation++;
      this.controller?.abort();
      this.intersection?.disconnect();
    }
  }

  customElements.define('external-stock', ExternalStock);
})();
